import axios, { AxiosInstance, AxiosError } from 'axios'
import { appConfig } from '@/config/app'

import type { User } from '@/types/user'
import type {
  ActivateTrialResponse,
  AdminManageBalanceResponse,
  AdminStatsResponse,
  BalanceResponse,
  ConfigsResponse,
  CreatePaymentRequest,
  CreatePaymentResponse,
  GetPaymentResponse,
  MySubscriptionsResponse,
  PackageByIdResponse,
  PackagesResponse,
  PaymentsResponse,
  ProfileResponse,
  ReferralEarningsResponse,
  ReferralDetailsResponse,
  InstallGuideSettingsResponse,
  PurchaseWithBalanceResponse,
  PurchaseSubscriptionWithBalanceRequest,
  PurchaseSubscriptionWithBalanceResponse,
  PurchaseSubscriptionFreeRequest,
  PurchaseSubscriptionFreeResponse,
  ReferralStatsResponse,
  ReferralListResponse,
  SearchUserResponseItem,
  TelegramLoginResponse,
  TopupBalanceResponse,
  TrialStatusResponse,
  TransactionsResponse,
} from '@/types/api'

interface ApiConfig {
  baseURL: string
  timeout: number
  user?: User | null
  token?: string | undefined
}

interface RequestOptions {
  signal?: AbortSignal
}

export interface SubscriptionDeviceSessionDto {
  device_hash: string
  user_agent?: string | null
  last_ip?: string | null
  country?: string | null
  first_seen_at?: string | null
  last_seen_at?: string | null
}

const normalizePathId = (value: string): string => encodeURIComponent(String(value || '').trim())

const normalizePaginationNumber = (value: number, fallback: number): number => {
  if (!Number.isFinite(value)) {
    return fallback
  }

  return Math.max(0, Math.floor(value))
}

const normalizeSearchQuery = (value: string): string => String(value || '')
  .trim()
  .replace(/[\u0000-\u001F\u007F]/g, '')
  .slice(0, 128)

class ApiClient {
  private client: AxiosInstance
  private config: ApiConfig

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = {
      baseURL: config.baseURL || appConfig.api.baseUrl,
      timeout: config.timeout || appConfig.api.requestTimeoutMs,
      user: config.user || null,
      token: config.token || undefined,
    }

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
    })

    // Добавить interceptor для авторизации
    this.client.interceptors.request.use((cfg) => {
      if (this.config.token) {
        cfg.headers.Authorization = `Bearer ${this.config.token}`
      }
      return cfg
    })

    // Обработка ошибок
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Глобальный logout при 401
          if (typeof window !== 'undefined') {
            localStorage.removeItem('vpnToken')
            // Можно добавить очистку других данных, если нужно
            window.location.href = '/login'
          }
        }
        console.error('API Error:', error.response?.status, error.message)
        return Promise.reject(error)
      }
    )
  }

  setToken(token: string): void {
    this.config.token = token
  }

  setUser(user: User | null): void {
    this.config.user = user
  }

  async getConfigs(): Promise<ConfigsResponse> {
    const response = await this.client.get<ConfigsResponse>('/configs')
    return response.data
  }

  async getConfig(id: string): Promise<unknown> {
    const response = await this.client.get<unknown>(`/configs/${normalizePathId(id)}`)
    return response.data
  }

  async createPayment(data: CreatePaymentRequest): Promise<CreatePaymentResponse> {
    const response = await this.client.post<CreatePaymentResponse>('/payments', data)
    return response.data
  }

  async registerPaymentHandoff(handoffId: string, payload: { paymentUrl: string; paymentId: string }): Promise<void> {
    await this.client.put(`/payments/handoff/${normalizePathId(handoffId)}`, payload)
  }

  async getPayments(): Promise<PaymentsResponse> {
    const response = await this.client.get<PaymentsResponse>('/payments/user/me')
    return response.data
  }

  async getPayment(id: string): Promise<GetPaymentResponse> {
    const response = await this.client.get<GetPaymentResponse>(`/payments/${normalizePathId(id)}`)
    return response.data
  }

  async syncPaymentStatus(id: string): Promise<GetPaymentResponse> {
    const response = await this.client.post<GetPaymentResponse>(`/payments/${normalizePathId(id)}/sync`)
    return response.data
  }

  async getStats(): Promise<AdminStatsResponse> {
    const response = await this.client.get<AdminStatsResponse>('/payments/admin/stats')
    return response.data
  }

  async login(initData: string, startParam?: string): Promise<TelegramLoginResponse> {
    const response = await this.client.post<TelegramLoginResponse>('/auth/telegram', { initData, start_param: startParam })
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
  }

  async getProfile(): Promise<User> {
    const response = await this.client.get<ProfileResponse>('/me')
    return response.data.user
  }

  async getReferralStats(options: RequestOptions = {}): Promise<ReferralStatsResponse> {
    const response = await this.client.get<ReferralStatsResponse>('/referrals/stats', options)
    return response.data
  }

  async getReferralList(options: RequestOptions = {}): Promise<ReferralListResponse> {
    const response = await this.client.get<ReferralListResponse>('/referrals/list', options)
    return response.data
  }

  async getReferralEarnings(limit: number = 100, options: RequestOptions = {}): Promise<ReferralEarningsResponse> {
    const normalizedLimit = Math.min(200, Math.max(1, normalizePaginationNumber(limit, 100)))

    const response = await this.client.get<ReferralEarningsResponse>('/referrals/earnings', {
      ...options,
      params: { limit: normalizedLimit },
    })
    return response.data
  }

  async getReferralDetails(referralTelegramId: string, options: RequestOptions = {}): Promise<ReferralDetailsResponse> {
    const response = await this.client.get<ReferralDetailsResponse>(`/referrals/${normalizePathId(referralTelegramId)}/details`, options)
    return response.data
  }

  async getPackages(): Promise<PackagesResponse> {
    const response = await this.client.get<PackagesResponse>('/packages')
    return response.data
  }

  async getPackageById(id: string): Promise<PackageByIdResponse> {
    const response = await this.client.get<PackageByIdResponse>(`/packages/${normalizePathId(id)}`)
    return response.data
  }

  async getMySubscriptions(): Promise<MySubscriptionsResponse> {
    const response = await this.client.get<MySubscriptionsResponse>('/me/subscriptions')
    return response.data
  }

  async getSubscriptionDeviceSessions(subscriptionId: string): Promise<{ sessions: SubscriptionDeviceSessionDto[] }> {
    const response = await this.client.get<{ sessions?: SubscriptionDeviceSessionDto[] }>(`/me/subscriptions/${normalizePathId(subscriptionId)}/sessions`)
    return { sessions: Array.isArray(response.data.sessions) ? response.data.sessions : [] }
  }

  async terminateSubscriptionDeviceSession(
    subscriptionId: string,
    deviceHash: string
  ): Promise<{ success?: boolean; terminated?: boolean }> {
    const response = await this.client.post<{ success?: boolean; terminated?: boolean }>(
      `/me/subscriptions/${normalizePathId(subscriptionId)}/sessions/${normalizePathId(deviceHash)}/terminate`
    )

    return response.data
  }

  async updateSubscriptionInactiveSessionTimeout(
    subscriptionId: string,
    timeoutSeconds: number
  ): Promise<{ subscription?: { id: string; inactive_session_ttl_seconds: number } }> {
    const normalizedTimeout = Math.max(0, Math.floor(Number(timeoutSeconds || 0)))

    const response = await this.client.patch<{ subscription?: { id: string; inactive_session_ttl_seconds: number } }>(
      `/me/subscriptions/${normalizePathId(subscriptionId)}/inactive-session-timeout`,
      { inactive_session_ttl_seconds: normalizedTimeout }
    )

    return response.data
  }

  // Transactions & Balance
  async getBalance(): Promise<BalanceResponse> {
    const response = await this.client.get<BalanceResponse>('/transactions/balance')
    return response.data
  }

  async getTransactions(limit: number = 10, offset: number = 0): Promise<TransactionsResponse> {
    const normalizedLimit = Math.min(100, Math.max(1, normalizePaginationNumber(limit, 10)))
    const normalizedOffset = normalizePaginationNumber(offset, 0)

    const response = await this.client.get<TransactionsResponse>('/transactions', {
      params: { limit: normalizedLimit, offset: normalizedOffset }
    })
    return response.data
  }

  async topupBalance(amount: number): Promise<TopupBalanceResponse> {
    const response = await this.client.post<TopupBalanceResponse>('/transactions/topup', { amount })
    return response.data
  }

  async purchaseWithBalance(configId: string, amount: number, durationDays: number = 30): Promise<PurchaseWithBalanceResponse> {
    const response = await this.client.post<PurchaseWithBalanceResponse>('/transactions/purchase-config', {
      configId,
      amount,
      durationDays
    })
    return response.data
  }

  async purchaseSubscriptionWithBalance(
    data: PurchaseSubscriptionWithBalanceRequest
  ): Promise<PurchaseSubscriptionWithBalanceResponse> {
    const response = await this.client.post<PurchaseSubscriptionWithBalanceResponse>(
      '/transactions/purchase-subscription-with-balance',
      data
    )

    return response.data
  }

  async purchaseSubscriptionFree(
    data: PurchaseSubscriptionFreeRequest
  ): Promise<PurchaseSubscriptionFreeResponse> {
    const response = await this.client.post<PurchaseSubscriptionFreeResponse>(
      '/transactions/purchase-subscription-free',
      data
    )
    return response.data
  }

  async getTrialStatus(options: RequestOptions = {}): Promise<TrialStatusResponse> {
    const response = await this.client.get<TrialStatusResponse>('/transactions/trial/status', options)
    return response.data
  }

  async activateTrial(): Promise<ActivateTrialResponse> {
    const response = await this.client.post<ActivateTrialResponse>('/transactions/trial/activate')
    return response.data
  }

  async adminManageBalance(userId: string, amount: number, action: 'add' | 'subtract', description: string): Promise<AdminManageBalanceResponse> {
    const response = await this.client.post<AdminManageBalanceResponse>('/transactions/admin/manage-balance', {
      user_id: userId,
      amount,
      action,
      description
    })
    return response.data
  }

  async searchUsers(query: string): Promise<SearchUserResponseItem[]> {
    const normalizedQuery = normalizeSearchQuery(query)
    const response = await this.client.get<SearchUserResponseItem[]>('/users/search', {
      params: { q: normalizedQuery }
    })
    return response.data
  }

  async getMiniAppMaintenance(initData?: string): Promise<{ maintenance?: { enabled?: boolean; message?: string; blocked?: boolean } }> {
    try {
      const response = await this.client.get<{ maintenance?: { enabled?: boolean; message?: string; blocked?: boolean } }>('/miniapp/maintenance', {
        params: initData ? { initData } : undefined,
      })
      return response.data
    } catch {
      return { maintenance: { enabled: false, message: '', blocked: false } }
    }
  }

  async getMiniAppSettings(): Promise<{
    fullscreenMode?: 'enabled' | 'disabled'
    externalLinks?: {
      telegramChannelUrl?: string
      telegramCommunityUrl?: string
      supportBotUrl?: string
    }
  }> {
    const response = await this.client.get<{
      miniAppSettings?: {
        fullscreenMode?: 'enabled' | 'disabled'
        externalLinks?: {
          telegramChannelUrl?: string
          telegramCommunityUrl?: string
          supportBotUrl?: string
        }
      }
    }>('/miniapp/settings')

    return response.data.miniAppSettings || {}
  }

  async getInstallGuideSettings(options: RequestOptions = {}): Promise<InstallGuideSettingsResponse> {
    const response = await this.client.get<InstallGuideSettingsResponse>('/install-guide/settings', options)
    return response.data
  }

  async getLegalPage(type: string): Promise<{ title: string; text: string }> {
    const response = await this.client.get<{ title: string; text: string }>(`/legal/${normalizePathId(type)}`)
    return response.data
  }

  async regenerateSubscriptionLink(subscriptionId: string): Promise<{ subscription?: { id: string; access_key?: string | null } }> {
    const response = await this.client.post<{ subscription?: { id: string; access_key?: string | null } }>(`/me/subscriptions/${normalizePathId(subscriptionId)}/regenerate-link`)
    return response.data
  }

  async resetSubscriptionSessions(subscriptionId: string): Promise<{ success?: boolean }> {
    const response = await this.client.post<{ success?: boolean }>(`/me/subscriptions/${normalizePathId(subscriptionId)}/reset-sessions`)
    return response.data
  }


}

export const apiClient = new ApiClient()

export default apiClient
